import { ActionItem, ButtonActionItem } from '@stylehaven/components';
import { uniq } from 'lodash-es';
import { Observable, Observer } from 'rxjs';
import {
    DetermineEnablePredicate,
    DetermineEnablePredicateLenient
} from '../../../helpers/determine-enable-predicate.helper';
import { ContextHolder, ContextMetadata, ContextReason } from '../metadata.entity';
import { RecordActionItem } from './record-action-item.class';

export class ContextHolderActionItem<T extends ContextHolder | ContextHolder[]> extends RecordActionItem<T> {
    private _contextAction: (keyof ContextMetadata)[];

    callback: (record: T, observer: Observer<any>) => void = () => {};

    constructor(
        label: string | Observable<string>,
        contextAction: keyof ContextMetadata | (keyof ContextMetadata)[],
        disabledInfoMsg: string
    ) {
        super(label);
        this._disabledInfoMsg = disabledInfoMsg;
        if (Array.isArray(contextAction)) {
            this._contextAction = contextAction;
        } else {
            this._contextAction = [contextAction];
        }
    }

    protected createActionItem(
        shouldEnable: boolean | undefined,
        label: string,
        record: T,
        observer: Observer<any>,
        template: Partial<ActionItem>
    ): ButtonActionItem | null {
        const items: ContextHolder[] = this.toArray(record);

        const context: (boolean | undefined)[] = items.map(current =>
            DetermineEnablePredicateLenient(
                this._contextAction.map(context => current?.Context?.[context] as boolean | undefined)
            )
        );
        const enabled = DetermineEnablePredicate(context, shouldEnable);
        if (enabled === undefined) {
            return null;
        }
        let disabledInfoMsg: string | undefined = undefined;
        if (!enabled) {
            const reason = this.getReasons(items);
            disabledInfoMsg = reason && reason.length > 0 ? uniq(reason).join() : this._disabledInfoMsg;
        }

        return {
            ...template,
            label,
            disabled: !enabled,
            eventListener: event => {
                if (template.eventListener) {
                    template.eventListener(event);
                }
                this.callback(record!, observer);
            },
            disabledInfoMsg
        };
    }

    private getReasons(items: ContextHolder[]) {
        return items.flatMap(contextRecord => {
            return this._contextAction
                .flatMap(action => (contextRecord?.Context?.Reasons ? contextRecord.Context.Reasons[action] : []))
                .filter((reason): reason is ContextReason => !!reason)
                .map(reason => reason.Value);
        });
    }

    private toArray(record: T): ContextHolder[] {
        if (Array.isArray(record)) {
            return record;
        } else {
            return [record as ContextHolder];
        }
    }
}
